import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, input, isDevMode, OnChanges, OnDestroy, OnInit, output, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  HoverCardDirective,
  HoverCardPosition,
} from '../../../directives/hover-card.directive';
import { BillingHistoryRequest } from '../../../models/billing-history.models';
import {
  CAPACITY_STATUS_CODES,
  CapacityStatusOption,
  CreateResourceCapacityRequest,
  ResourceCapacitiesResponseDto,
  WeeklyCapacity,
  WeekPeriod,
} from '../../../models/capacity.models';
import { LookupTypeCodes, LookupValue } from '../../../models/lookup.models';
import { CapacityService } from '../../../services/capacity.service';
import { LookupService } from '../../../services/lookup.service';
import { ResourceService } from '../../../services/resource.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError, toApiError } from '../../../utils/api-error.utils';
import { retryTransient } from '../../../utils/http-retry.utils';
import { CapacityLegendComponent } from '../capacity-legend/capacity-legend.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../confirmation-dialog/confirmation-dialog.component';
import { UnsavedChangesDialogComponent } from '../unsaved-changes-dialog/unsaved-changes-dialog.component';

const MILLISECONDS_PER_DAY = 86400000;
const DAYS_IN_WEEK = 7;
// First wait before re-sending a transient failure; doubles on the second attempt.
const RETRY_BASE_DELAY_MS = 250;
// One sentence per validation rule, shared by the save guard's toast, the check icon's tooltip
// and the unsaved-changes dialog, so the user reads the same words wherever the rule stops them.
// Ends in a full stop because the tooltip joins it to the hours sentence when both rules fail.
const STATUS_REQUIRED_MESSAGE =
  'Please select a capacity status for every period you changed.';
// Shown under the status dropdown itself.
const STATUS_REQUIRED_FIELD_MESSAGE = 'Select a capacity status';
// No direction in the wording: it is also shown in a modal dialog, where "below" would point
// at the dialog's own buttons rather than the field behind it.
const EXPECTED_HOURS_INVALID_MESSAGE =
  'Fix the highlighted expected hours before saving';
// Shown under the hours field for a changed period the office requires hours for.
const EXPECTED_HOURS_REQUIRED_MESSAGE =
  'Expected hours are required for your office and practice area';

/** Explains why a period past its first week cannot be edited. */
function entryWindowClosedMessageFor(capacityInterval: number): string {
  return (
    `Capacity for the current ${capacityInterval}-week period can only be ` +
    'entered during the first week of the period.'
  );
}

@Component({
  selector: 'app-capacity-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CapacityLegendComponent,
    HoverCardDirective,
  ],
  templateUrl: './capacity-management.component.html',
  styleUrls: ['./capacity-management.component.scss'],
})
export class CapacityManagementComponent
  implements OnInit, OnChanges, OnDestroy
{
  private capacityService = inject(CapacityService);
  private resourceService = inject(ResourceService);
  private lookupService = inject(LookupService);
  private dialog = inject(MatDialog);
  private snackbarService = inject(SnackbarService);
  private cdr = inject(ChangeDetectorRef);

  readonly employeeId = input<string>('');
  readonly isReadOnly = input<boolean>(false);
  readonly canEditCapacitySettings = input<boolean>(false); // Controls visibility of the Global switch and the read-only period text

  readonly editModeRequested = output<void>(); // Emit when user wants to enter edit mode

  currentPeriod: WeekPeriod;
  periodLength: string = 'ONE_WEEK';
  // Raw interval length in weeks, as returned by the API: the cadence the entry-window
  // lock is judged on ("interval > 1") and the one the read-only period text reports.
  // periodLength is kept alongside it because period generation still reads that label,
  // and processPeriodLength maps only 1 and 2 onto it.
  capacityInterval = 1;
  // Explains a closed entry window. Built once per load rather than per binding, since
  // it varies only with capacityInterval.
  entryWindowClosedMessage = '';
  // UTC day of the cadence anchor; null when the resource has no interval configured.
  private intervalAnchorDay: number | null = null;
  // currentInterval()'s result, held for the UTC day it was computed on.
  private cachedInterval: {
    day: number;
    interval: { startDay: number; daysIn: number };
  } | null = null;
  isOverallMode = false; // Track if "Overall" mode is active
  overallCapacityStatus: LookupValue | null = null; // Overall capacity status
  originalOverallCapacityStatus: LookupValue | null = null; // Original overall status for unsaved changes
  capacityStatusLookups: LookupValue[] = []; // Loaded lookup values for capacity statuses
  isSaving = false;
  isInitialLoading = true;
  isPeriodLoading = false;
  isBillingDataLoading = false; // Track if billing data is being fetched
  isCheckingOverallMode = false; // Track if we're checking for overall capacity on edit mode entry
  periods: WeekPeriod[] = [];
  capacityData: { [key: string]: WeeklyCapacity } = {};
  selectedPeriodKey: string | null = null; // Tracks selected/highlighted period

  /**
   * Whether this resource's office and practice area make expected hours mandatory, as reported by
   * the capacity read.
   *
   * Advisory: the API enforces the rule on every write regardless, so this only decides whether the
   * field is marked required and blocks the save early. False while the `expected-period-hours`
   * flag is off, which is why the field is optional for everyone until that flag flips.
   */
  expectedHoursRequired = false;

  /**
   * Per-period errors for a bad typed value ("abc", 7.5, 1000), keyed by period; null when the
   * typed value is fine. An empty field is not an error of its own: whether hours are missing
   * depends on whether the period will be sent, which missingRequiredHoursError derives.
   * getExpectedHoursError reports both.
   */
  expectedHoursErrors: { [key: string]: string | null } = {};

  /**
   * What the user has typed per period, kept as the raw string.
   *
   * Deliberately not parsed straight into `capacityData`: "" and "abc" are both "not a number", but
   * only the first is a request to clear. Holding the text lets the field show exactly what was
   * typed while the parsed value drives the save.
   */
  expectedHoursInput: { [key: string]: string } = {};
  private destroy$ = new Subject<void>();
  private cancelRequest$ = new Subject<void>(); // Cancel pending HTTP requests
  /**
   * Set in ngOnDestroy. Writes are NOT unsubscribed on destroy: HttpClient aborts the
   * request when the subscription ends, and both saveAndExitEditMode and canDeactivate
   * start a save and then let this card go, so unsubscribing would lose the save. The
   * requests are left to finish and their handlers check this flag instead of touching
   * a component that no longer exists.
   */
  private isDestroyed = false;

  // Edit mode state - starts in view mode even when user has permission to edit
  isEditMode = false;
  // Track original data to detect unsaved changes
  private originalCapacityData: { [key: string]: WeeklyCapacity } = {};

  // Status legend hover state
  showStatusLegend = false;
  statusLegendPosition: HoverCardPosition = { x: 0, y: 0 };

  // Header info hover state
  showHeaderInfo = false;
  headerInfoPosition: HoverCardPosition = { x: 0, y: 0 };

  // Capacity status options for display (populated from lookup values)
  capacityStatusOptions: CapacityStatusOption[] = [];

  // Get selectable capacity status options (excludes MIXED and UNKNOWN as they're not user-selectable)
  get selectableCapacityStatusOptions(): CapacityStatusOption[] {
    return this.capacityStatusOptions.filter(
      (option) =>
        option.value !== CAPACITY_STATUS_CODES.MIXED &&
        option.value !== CAPACITY_STATUS_CODES.UNKNOWN,
    );
  }

  constructor() {
    // Initialize current period to this week
    this.currentPeriod = this.getCurrentWeekPeriod();
  }

  ngOnInit(): void {
    // Load capacity status lookup values
    this.lookupService
      .getLookupValues(LookupTypeCodes.RESOURCE_CAPACITY_STATUS)
      .subscribe((values) => {
        this.capacityStatusLookups = values;
        this.capacityStatusOptions = values
          .filter((v) => v.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((v) => ({ value: v.code, label: v.name }));
      });

    // Generate the 4 periods to display first
    this.generatePeriods();
    this.ensureSelectedPeriod();

    // Load capacity data if employeeId is already available
    // This will also load the currentCapacityStatusOverride from the response
    if (this.employeeId()) {
      this.loadCapacityData();
    } else {
      this.isInitialLoading = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to employeeId changes
    if (changes['employeeId'] && !changes['employeeId'].firstChange) {
      const newEmployeeId = changes['employeeId'].currentValue;
      if (
        newEmployeeId &&
        newEmployeeId !== changes['employeeId'].previousValue
      ) {
        this.isInitialLoading = true;
        // The previous employee's cadence must not decide this one's entry window
        this.captureCadence(1, null);
        this.loadCapacityData();
      }
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    // Reads can be abandoned safely; writes deliberately are not (see isDestroyed).
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onHeaderInfoShow(position: HoverCardPosition): void {
    this.headerInfoPosition = position;
    this.showHeaderInfo = true;
    this.cdr.detectChanges();
  }

  onHeaderInfoHide(): void {
    this.showHeaderInfo = false;
  }

  toggleEditMode(): void {
    if (this.isEditMode) {
      // Switching from edit mode to view mode - check for unsaved changes
      if (this.hasUnsavedChanges()) {
        this.showUnsavedChangesDialog();
      } else {
        // Nothing to save, but a value typed and then deleted (or "abc") leaves an error and
        // the typed text behind. Clear them, or the next edit session starts blocked.
        this.resetExpectedHoursState();
        this.isEditMode = false;
        this.isOverallMode = false; // Reset overall mode when exiting edit mode
      }
    } else {
      // Request to enter edit mode - parent will coordinate
      this.editModeRequested.emit();
    }
  }

  /**
   * Called by parent to actually enter edit mode after coordination
   */
  enterEditMode(): void {
    this.isEditMode = true;
    this.isCheckingOverallMode = true; // Start loading
    this.originalCapacityData = JSON.parse(JSON.stringify(this.capacityData));

    // Check if user has an overall capacity override set
    this.checkAndLoadOverallCapacity();

    // If viewing past periods, navigate to current week
    if (this.isViewingPastPeriods()) {
      this.navigateToCurrentWeek();
    }
  }

  /**
   * Try to exit edit mode, showing dialog if there are unsaved changes.
   * Returns a promise that resolves to true if exited, false if user cancelled.
   */
  tryExitEditMode(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.isEditMode) {
        resolve(true);
        return;
      }

      if (!this.hasUnsavedChanges()) {
        this.resetExpectedHoursState();
        this.isEditMode = false;
        this.isOverallMode = false;
        this.cdr.detectChanges();
        resolve(true);
        return;
      }

      const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
        width: '500px',
        disableClose: true,
        data: {
          entityName: 'Capacity',
          hasValidationErrors: this.hasAnyValidationErrors(),
          validationErrorMessage: this.getSaveButtonTooltip(),
        },
      });

      dialogRef.afterClosed().subscribe((result: string) => {
        if (result === 'save') {
          if (!this.hasAnyValidationErrors()) {
            this.onSaveCapacities();
            this.isEditMode = false;
            this.isOverallMode = false;
            this.cdr.detectChanges();
            resolve(true);
          } else {
            resolve(false);
          }
        } else if (result === 'discard') {
          this.cancelEdit();
          resolve(true);
        } else {
          // 'cancel' - don't exit
          resolve(false);
        }
      });
    });
  }

  cancelEdit(): void {
    this.restoreOriginalData();
    this.isEditMode = false;
    this.isOverallMode = false;
    this.cdr.detectChanges();
  }

  saveAndExitEditMode(): void {
    if (this.hasAnyValidationErrors()) {
      return;
    }

    if (this.hasUnsavedChanges()) {
      this.onSaveCapacities();
    }
    this.isEditMode = false;
    this.isOverallMode = false;
  }

  hasUnsavedChanges(): boolean {
    // Check for overall capacity changes
    if (this.isOverallMode && this.hasOverallUnsavedChanges()) {
      return true;
    }

    // Check for individual period changes (only if not in overall mode)
    if (!this.isOverallMode) {
      const originalKeys = Object.keys(this.originalCapacityData);
      const currentKeys = Object.keys(this.capacityData);

      // Check if number of entries changed
      if (originalKeys.length !== currentKeys.length) {
        return true;
      }

      // Check each entry for changes, by the same comparison the save payload uses
      for (const key of currentKeys) {
        if (!this.originalCapacityData[key] || this.isCapacityChangedAt(key)) {
          return true;
        }
      }
    }

    return false;
  }

  showUnsavedChangesDialog(): void {
    const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityName: 'Capacity',
        hasValidationErrors: this.hasAnyValidationErrors(),
        validationErrorMessage: this.getSaveButtonTooltip(),
      },
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result === 'discard') {
        this.restoreOriginalData();
        this.isEditMode = false;
        this.isOverallMode = false; // Reset overall mode when discarding changes
        this.cdr.detectChanges();
      } else if (result === 'save' && !this.hasAnyValidationErrors()) {
        // The dialog only offers Save when nothing is invalid, but the save would refuse
        // to run anyway, so never leave edit mode on the strength of a 'save' alone.
        this.onSaveCapacities();
        this.isEditMode = false;
        this.isOverallMode = false; // Reset overall mode when saving changes
      }
      // If 'cancel', do nothing - stay in edit mode
    });
  }

  showNavigationUnsavedChangesDialog(
    direction: 'previous' | 'next' | 'current',
  ): void {
    const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityName: 'Capacity',
        hasValidationErrors: this.hasAnyValidationErrors(),
        validationErrorMessage: this.getSaveButtonTooltip(),
      },
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result === 'discard') {
        this.restoreOriginalData();
        // Proceed with navigation
        this.performNavigation(direction);
      } else if (result === 'save') {
        // Save changes first, then navigate
        this.saveAndNavigate(direction);
      }
      // If 'cancel', do nothing - stay on current period
    });
  }

  private performNavigation(direction: 'previous' | 'next' | 'current'): void {
    this.isPeriodLoading = true;

    if (direction === 'current') {
      const now = new Date();
      const currentWeekStart = this.getStartOfWeek(now);
      this.updatePeriodsStartingFrom(currentWeekStart);
    } else {
      const weeksToMove = this.periodLength === 'ONE_WEEK' ? 4 : 8;
      const newStartDate = new Date(this.periods[0].startDate);
      const daysToMove = weeksToMove * 7;
      newStartDate.setDate(
        newStartDate.getDate() +
          (direction === 'next' ? daysToMove : -daysToMove),
      );
      this.updatePeriodsStartingFrom(newStartDate);
    }
  }

  private saveAndNavigate(direction: 'previous' | 'next' | 'current'): void {
    // Store the navigation direction for after save completes
    this.pendingNavigation = direction;
    // Call the existing save method
    if (this.isOverallMode) {
      this.onSaveOverallCapacity();
    } else {
      this.onSaveCapacities();
    }
  }

  private pendingNavigation: 'previous' | 'next' | 'current' | null = null;

  /**
   * Performs a navigation that was queued behind a save, if there is one, and reports
   * whether it did. Every path that completes a save has to call this, including the
   * ones that send no request — otherwise the stale direction fires on a later save and
   * moves the user unexpectedly.
   */
  private flushPendingNavigation(): boolean {
    if (!this.pendingNavigation) {
      return false;
    }

    const direction = this.pendingNavigation;
    this.pendingNavigation = null;
    this.performNavigation(direction);
    return true;
  }

  /**
   * Drops a navigation queued behind a save that did not go through. The user stays put
   * to deal with the failure, so the direction must not survive to a later save.
   */
  private discardPendingNavigation(): void {
    this.pendingNavigation = null;
  }

  canDeactivate(): boolean | Observable<boolean> {
    // If not in edit mode, always allow navigation
    if (!this.isEditMode) {
      return true;
    }

    // If in edit mode but no unsaved changes, allow navigation
    if (!this.hasUnsavedChanges()) {
      return true;
    }

    // Show dialog and return an observable that resolves based on user choice
    const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityName: 'Capacity',
        hasValidationErrors: this.hasAnyValidationErrors(),
        validationErrorMessage: this.getSaveButtonTooltip(),
      },
    });

    return dialogRef.afterClosed().pipe(
      map((result: string) => {
        if (result === 'discard') {
          this.restoreOriginalData();
          this.isEditMode = false;
          this.isOverallMode = false; // Reset overall mode when discarding changes
          this.cdr.detectChanges();
          return true;
        } else if (result === 'save' && !this.hasAnyValidationErrors()) {
          // Save changes and allow navigation. An invalid card is treated as a cancel:
          // the save would refuse to run, and navigating away would strand the field.
          this.onSaveCapacities();
          this.isEditMode = false;
          this.isOverallMode = false; // Reset overall mode when saving changes
          return true;
        } else {
          // Cancel - stay on page
          return false;
        }
      }),
    );
  }

  onStatusLegendShow(position: HoverCardPosition): void {
    this.statusLegendPosition = position;
    this.showStatusLegend = true;
    this.cdr.detectChanges();
  }

  onStatusLegendHide(): void {
    this.showStatusLegend = false;
  }

  /**
   * Helper method to create a capacity data object
   */
  private createCapacityData(
    period: WeekPeriod,
    status: LookupValue | null,
    notes: string = '',
  ): WeeklyCapacity {
    return {
      employeeId: this.employeeId(),
      weekStartDate: period.startDate.toISOString(),
      weekEndDate: period.endDate.toISOString(),
      capacityStatus: status,
      notes: notes,
    };
  }

  private getCurrentWeekPeriod(): WeekPeriod {
    const now = new Date();
    const startOfWeek = this.getStartOfWeek(now);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return {
      startDate: startOfWeek,
      endDate: endOfWeek,
      weekNumber: this.getWeekNumber(startOfWeek),
      year: startOfWeek.getFullYear(),
      displayText: this.formatWeekDisplay(startOfWeek, endOfWeek),
    };
  }

  /**
   * Today at local midnight, the reference point for the per-period checks: isPastPeriod,
   * isCurrentPeriod, isFuturePeriod and the billing fetch's "skip the future" test.
   *
   * parseApiDate discards the time, so a period ending today has an endDate of today at 00:00.
   * Comparing that against a `new Date()` carrying the current time puts "now" past the end of
   * its own period for the whole of its last day. Those checks share this helper so they cannot
   * drift apart. The header label and the navigation guards (getPeriodLabel,
   * isViewingPastPeriods, canNavigatePrevious) anchor on the start of the current week instead.
   */
  private startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * True when a period ended before today.
   *
   * Decided on the end date, not the start: on a two-week cadence the current period starts a
   * week ago, and anchoring on the start of the current week would call it past.
   */
  isPastPeriod(period: WeekPeriod): boolean {
    return period.endDate.getTime() < this.startOfToday().getTime();
  }

  /**
   * Check if a period is after today (future period)
   */
  isFuturePeriod(period: WeekPeriod): boolean {
    return period.startDate > this.startOfToday();
  }

  /**
   * Check if any of the current periods being viewed is before the current week
   */
  private isViewingPastPeriods(): boolean {
    if (!this.periods || this.periods.length === 0) {
      return false;
    }
    const now = new Date();
    const currentWeekStart = this.getStartOfWeek(now);
    // Check if the first period shown is before current week
    return this.periods[0].startDate < currentWeekStart;
  }

  /**
   * Navigate to the current week
   */
  private navigateToCurrentWeek(): void {
    const currentWeekPeriod = this.getCurrentWeekPeriod();
    this.updatePeriodsStartingFrom(currentWeekPeriod.startDate);
  }

  /**
   * Check if the user can navigate to previous periods
   * Prevents going to past weeks when in edit mode
   */
  canNavigatePrevious(): boolean {
    if (this.isSaving) {
      return false;
    }

    // If in edit mode, don't allow navigating before current week
    if (this.isEditMode) {
      const now = new Date();
      const currentWeekStart = this.getStartOfWeek(now);
      // Check if the first period is at or after current week
      return this.periods[0].startDate >= currentWeekStart;
    }

    return true;
  }

  private getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = d.getDate() - day; // Calculate days to subtract to get to Sunday
    const result = new Date(d.setDate(diff));
    result.setHours(0, 0, 0, 0); // Normalize to midnight for consistent date comparisons
    return result;
  }

  private getWeekNumber(date: Date): number {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(
      ((d.getTime() - yearStart.getTime()) / MILLISECONDS_PER_DAY + 1) /
        DAYS_IN_WEEK,
    );
  }

  private formatWeekDisplay(startDate: Date, endDate: Date): string {
    const weekNum = this.getWeekNumber(startDate);
    const startStr = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const endStr = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `Week ${weekNum}: ${startStr} - ${endStr}`;
  }

  private generatePeriods(): void {
    this.periods = [];
    const startDate = this.currentPeriod
      ? this.currentPeriod.startDate
      : this.getStartOfWeek(new Date());

    for (let i = 0; i < 4; i++) {
      const periodStart = new Date(startDate);
      const weeksToAdd = this.periodLength === 'ONE_WEEK' ? i : i * 2;
      periodStart.setDate(startDate.getDate() + weeksToAdd * 7);

      const periodEnd = new Date(periodStart);
      const daysToAdd = this.periodLength === 'ONE_WEEK' ? 6 : 13; // 1 week = 7 days (0-6), 2 weeks = 14 days (0-13)
      periodEnd.setDate(periodStart.getDate() + daysToAdd);

      this.periods.push({
        startDate: periodStart,
        endDate: periodEnd,
        weekNumber: this.getWeekNumber(periodStart),
        year: periodStart.getFullYear(),
        displayText: this.formatPeriodDisplay(periodStart, periodEnd),
      });
    }
  }

  private ensureSelectedPeriod(): void {
    if (!this.periods || this.periods.length === 0) {
      this.selectedPeriodKey = null;
      return;
    }

    // Keep selection if still present; otherwise clear selection (no default)
    if (
      this.selectedPeriodKey &&
      this.periods.some(
        (p) => p.startDate.toISOString() === this.selectedPeriodKey,
      )
    ) {
      return;
    }
    this.selectedPeriodKey = null;
  }

  private formatPeriodDisplay(startDate: Date, endDate: Date): string {
    const startStr = startDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
    const endStr = endDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    return `${startStr} - ${endStr}`;
  }

  /**
   * Year, month and day of an ISO date the API sent, or null when it cannot be read.
   * Handles both "YYYY-MM-DD" and "YYYY-MM-DDTHH:mm:ss".
   */
  private parseApiDateParts(
    dateString: string | null | undefined,
  ): [number, number, number] | null {
    if (typeof dateString !== 'string') {
      return null;
    }

      const datePart = dateString.split('T')[0]; // Get just the date part
      const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) {
      return null;
      }

    // Date arithmetic rolls out-of-range parts over silently, so 2026-02-30 would
    // become March 2 — a plausible wrong answer. Round-trip to reject it instead.
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (
      probe.getUTCFullYear() !== year ||
      probe.getUTCMonth() !== month - 1 ||
      probe.getUTCDate() !== day
    ) {
      return null;
      }

    return [year, month, day];
  }

  private parseApiDate(dateString: string | null | undefined): Date {
    const parts = this.parseApiDateParts(dateString);

    if (parts) {
      // Parse as a local date to avoid timezone shifts; month is 0-indexed
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      if (!isNaN(date.getTime())) {
      return date;
      }
    }

      if (isDevMode()) {
      console.error('Date parsing error:', dateString);
      }
      // Return current date as fallback
      return new Date();
    }

  /**
   * Load capacity data from the API
   */
  private loadCapacityData(): void {
    if (this.isDestroyed || !this.validateEmployeeId()) {
      return;
    }

    const startDate = this.getCapacityStartDate();

    // Cancel any pending requests
    this.cancelRequest$.next();

    this.capacityService
      .getCapacities(this.employeeId(), startDate, 4)
      .pipe(retryTransient(), takeUntil(this.cancelRequest$))
      .subscribe({
        next: (response) => this.handleLoadCapacitySuccess(response),
        error: (error) => this.handleLoadCapacityError(error),
      });
  }

  /**
   * Validate that employee ID is present
   */
  private validateEmployeeId(): boolean {
    if (!this.employeeId()) {
      this.snackbarService.showError('Employee ID is required');
      this.isInitialLoading = false;
      this.isPeriodLoading = false;
      return false;
    }
    return true;
  }

  /**
   * Get the start date for capacity data request
   */
  private getCapacityStartDate(): string | undefined {
    return this.periods.length > 0
      ? this.periodStartAsUtc(this.periods[0])
      : undefined;
  }

  /**
   * Handle successful capacity data load
   */
  private handleLoadCapacitySuccess(
    response: ResourceCapacitiesResponseDto,
  ): void {
    // Read before the periods are mapped: the field's required marker depends on it, and a resource
    // whose office stops requiring hours must see that on the very next load.
    this.expectedHoursRequired = response.expectedHoursRequired === true;

    this.getBillingData(response);
    this.captureCadence(response.capacityInterval, response.intervalStartDate);
    this.processPeriodLength(response.capacityInterval);
    this.regeneratePeriodsFromResponse(response.resourceCapacities);
    this.mapCapacityDataFromResponse(response.resourceCapacities);

    // Store the current capacity status override from the response
    // This will be used when entering edit mode instead of making a separate API call
    if (response.currentCapacityStatusOverride) {
      this.overallCapacityStatus = response.currentCapacityStatusOverride;
      this.originalOverallCapacityStatus = JSON.parse(
        JSON.stringify(response.currentCapacityStatusOverride),
      );
    } else {
      this.overallCapacityStatus = null;
      this.originalOverallCapacityStatus = null;
    }

    this.finalizeLoadingState();
  }

  private getBillingData(response: any): void {
    const today = this.startOfToday();

    // Set loading flag
    this.isBillingDataLoading = true;

    // Build array of billing history requests for non-future periods
    const billingRequests: BillingHistoryRequest[] = [];

    response.resourceCapacities.forEach((capacity: any) => {
      // Validate required fields
      if (!capacity.employeeId || !capacity.startDate || !capacity.endDate) {
        console.error('Missing required billing data fields:', {
          employeeId: capacity.employeeId,
          startDate: capacity.startDate,
          endDate: capacity.endDate,
        });
        return;
      }

      // Skip future periods - billing data only applies to current and past periods
      const periodStart = new Date(capacity.startDate);
      if (periodStart > today) {
        return;
      }

      // Format dates to YYYY-MM-DD format expected by API
      const startDate = capacity.startDate.split('T')[0];
      const endDate = capacity.endDate.split('T')[0];

      billingRequests.push({
        employeeId: capacity.employeeId,
        startDate: startDate,
        endDate: endDate,
        // billedTypes omitted => all types
      });
    });

    // If no billing requests (all future periods), exit early
    if (billingRequests.length === 0) {
      this.isBillingDataLoading = false;
      return;
    }

    // Make single batch API call
    this.resourceService
      .getBillingHistoryList(billingRequests)
      .pipe(takeUntil(this.cancelRequest$))
      .subscribe({
        next: (billingHistories: any[]) => {
          this.isBillingDataLoading = false;
          // Map billing histories back to their corresponding capacities using the requested dates
          billingHistories.forEach((billingHistory: any) => {
            if (!billingHistory) return;

            // Find the matching capacity using the requested start and end dates from the response
            const matchingCapacity = response.resourceCapacities.find(
              (capacity: any) => {
                const capacityStartDate = capacity.startDate.split('T')[0];
                const capacityEndDate = capacity.endDate.split('T')[0];
                return (
                  capacityStartDate === billingHistory.requestedStartDate &&
                  capacityEndDate === billingHistory.requestedEndDate
                );
              },
            );

            if (!matchingCapacity) {
              console.warn(
                'No matching capacity found for billing history:',
                billingHistory,
              );
              return;
            }

            // Extract billing summary from the response (using correct property names).
            // The API returns these already rounded to one decimal, half-up, like every other
            // billing surface, so they are shown as is (8.5h, 8h).
            const totalBilled = billingHistory.totalBilledHours || 0;
            const totalUnbilled = billingHistory.totalNonbilledHours || 0;
            const totalProBono = billingHistory.totalProbonoHours || 0;
            const total = billingHistory.totalHours || 0;

            // Store billing info on the capacity object
            matchingCapacity.billingInfo = {
              billed: totalBilled,
              unbilled: totalUnbilled,
              proBono: totalProBono,
              total: total,
            };
          });
        },
        error: (error: any) => {
          this.isBillingDataLoading = false;
          if (isDevMode()) {
            console.error('Failed to load billing history:', error);
          }
          // Set default billing info on error for all requested capacities
          response.resourceCapacities.forEach((capacity: any) => {
            const periodStart = new Date(capacity.startDate);
            // Only set default for non-future periods that we attempted to fetch
            if (
              periodStart <= today &&
              capacity.employeeId &&
              capacity.startDate &&
              capacity.endDate
            ) {
              capacity.billingInfo = {
                billed: 0,
                unbilled: 0,
                proBono: 0,
                total: 0,
              };
            }
          });
        },
      });
  }

  /**
   * Record the cadence the entry-window rule is judged against. The anchor is read
   * straight from the response so client and API agree on which period is current, no
   * matter which date the load happened to request.
   */
  private captureCadence(
    capacityInterval: number,
    intervalStartDate: string | null,
  ): void {
    this.capacityInterval = capacityInterval || 1;
    this.intervalAnchorDay = this.apiDateToDay(intervalStartDate);
    this.cachedInterval = null;
    this.entryWindowClosedMessage = entryWindowClosedMessageFor(
      this.capacityInterval,
    );
    // The response also carries intervalWeekOffset, but the API computes it from the
    // date the load requested — which after the first load is the already-normalized
    // interval start, reporting week 1. The anchor is stable, so derive from that.
  }

  /**
   * Process period length from API response
   */
  private processPeriodLength(capacityInterval: number): void {
    if (capacityInterval === 1) {
      this.periodLength = 'ONE_WEEK';
    } else if (capacityInterval === 2) {
      this.periodLength = 'TWO_WEEKS';
    }
  }

  /**
   * Regenerate periods based on API response
   */
  private regeneratePeriodsFromResponse(resourceCapacities: any[]): void {
    this.periods = resourceCapacities.map((capacity) => {
      const startDate = this.parseApiDate(capacity.startDate);
      const endDate = this.parseApiDate(capacity.endDate);

      return {
        startDate: startDate,
        endDate: endDate,
        weekNumber: this.getWeekNumber(startDate),
        year: startDate.getFullYear(),
        displayText: this.formatPeriodDisplay(startDate, endDate),
      };
    });
  }

  /**
   * Map capacity data from API response to UI structure
   */
  private mapCapacityDataFromResponse(resourceCapacities: any[]): void {
    this.capacityData = {};
    this.expectedHoursErrors = {};
    this.expectedHoursInput = {};

    resourceCapacities.forEach((capacity) => {
      const startDate = this.parseApiDate(capacity.startDate);
      const periodKey = startDate.toISOString();

      const endDate = this.parseApiDate(capacity.endDate);
      const period: WeekPeriod = {
        startDate,
        endDate,
        weekNumber: this.getWeekNumber(startDate),
        year: startDate.getFullYear(),
        displayText: this.formatWeekDisplay(startDate, endDate),
      };
      const capacityData = this.createCapacityData(
        period,
        capacity.capacityStatus || null,
        capacity.comments || '',
      );
      // The API's interval total, kept as-is. Null stays null rather than becoming 0, because the
      // read path distinguishes "nothing reported" from "reported zero".
      capacityData.expectedHours = capacity.expectedHours ?? null;
      this.expectedHoursInput[periodKey] =
        this.expectedHoursTextFor(capacityData);

      // Store raw backend data for mixed status and tooltip details
      capacityData.rawData = capacity;
      this.capacityData[periodKey] = capacityData;
    });
  }

  /**
   * Finalize loading state and update original data if needed
   */
  private finalizeLoadingState(): void {
    this.isInitialLoading = false;
    this.isPeriodLoading = false;
    this.ensureSelectedPeriod();

    // Always update original data when loading fresh data from server
    // This ensures navigation doesn't falsely mark data as dirty
    this.originalCapacityData = JSON.parse(JSON.stringify(this.capacityData));
  }

  /**
   * Handle capacity data load error
   */
  private handleLoadCapacityError(error: any): void {
    if (isDevMode()) {
      console.error('Failed to load capacity data:', error);
    }

    const errorMessage = this.getErrorMessage(
      error,
      'Failed to load capacity data',
    );
    this.snackbarService.showError(errorMessage);
    this.isInitialLoading = false;
    this.isPeriodLoading = false;
  }

  isViewingCurrentPeriod(): boolean {
    if (this.periods.length === 0) return true;

    const now = new Date();
    const currentWeekStart = this.getStartOfWeek(now);
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);

    // Check if any of the displayed periods include the current week
    return this.periods.some((period) => {
      return (
        period.startDate <= currentWeekEnd && period.endDate >= currentWeekStart
      );
    });
  }

  getPeriodLabel(): string {
    if (this.periods.length === 0) return 'Current Period';

    if (this.isViewingCurrentPeriod()) {
      return 'Current Period';
    }

    const now = new Date();
    const currentWeekStart = this.getStartOfWeek(now);

    // If the first period is before the current week, we're viewing past periods
    if (this.periods[0].endDate < currentWeekStart) {
      return 'Past Period';
    }

    // Otherwise, we're viewing future periods
    return 'Future Period';
  }

  returnToCurrentPeriod(): void {
    // Check for unsaved changes in edit mode
    if (this.isEditMode && this.hasUnsavedChanges()) {
      this.showNavigationUnsavedChangesDialog('current');
      return;
    }

    // Cancel any pending requests
    this.cancelRequest$.next();

    this.isPeriodLoading = true;
    const now = new Date();
    const currentWeekStart = this.getStartOfWeek(now);
    this.updatePeriodsStartingFrom(currentWeekStart);
  }

  getCurrentDateRangeDisplay(): string {
    if (this.periods.length === 0) return '';

    const firstPeriod = this.periods[0];
    const lastPeriod = this.periods[this.periods.length - 1];

    const startStr = firstPeriod.startDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
    const endStr = lastPeriod.endDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    return `${startStr} - ${endStr}`;
  }

  onPreviousWeek(): void {
    // Check for unsaved changes in edit mode
    if (this.isEditMode && this.hasUnsavedChanges()) {
      this.showNavigationUnsavedChangesDialog('previous');
      return;
    }

    this.isPeriodLoading = true;
    // Move back by the appropriate interval
    const weeksToMove = this.periodLength === 'ONE_WEEK' ? 4 : 8;
    const newStartDate = new Date(this.periods[0].startDate);
    newStartDate.setDate(newStartDate.getDate() - weeksToMove * 7);
    this.updatePeriodsStartingFrom(newStartDate);
  }

  onNextWeek(): void {
    // Check for unsaved changes in edit mode
    if (this.isEditMode && this.hasUnsavedChanges()) {
      this.showNavigationUnsavedChangesDialog('next');
      return;
    }

    this.isPeriodLoading = true;
    // Move forward by the appropriate interval
    const weeksToMove = this.periodLength === 'ONE_WEEK' ? 4 : 8;
    const newStartDate = new Date(this.periods[0].startDate);
    newStartDate.setDate(newStartDate.getDate() + weeksToMove * 7);
    this.updatePeriodsStartingFrom(newStartDate);
  }

  private updatePeriodsStartingFrom(startDate: Date): void {
    // Update the base start date and regenerate periods
    this.currentPeriod.startDate = startDate;
    this.generatePeriods();
    this.ensureSelectedPeriod();
    this.loadCapacityData();
  }

  /**
   * Handle Overall mode switch toggle
   */
  onOverallModeToggle(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const newOverallMode = checkbox.checked;

    // Nothing about capacity changes while the window is closed, in either direction.
    // Leaving edit mode is the way out, and that is not gated on the window.
    if (this.isEntryWindowClosed()) {
      checkbox.checked = this.isOverallMode;
      this.snackbarService.showError(this.entryWindowClosedMessage);
      return;
    }

    if (newOverallMode) {
      // Turning Overall mode ON
      // Check for unsaved changes first
      if (this.hasUnsavedChanges()) {
        this.showEnableOverallModeWithUnsavedChangesDialog(checkbox);
      } else {
        this.isOverallMode = true;
      }
    } else {
      // Turning Overall mode OFF
      // Show confirmation dialog about turning off overall mode
      // This dialog handles both unsaved changes and clearing the override
      this.showClearOverallCapacityDialogForSwitch(checkbox);
    }
  }

  showEnableOverallModeWithUnsavedChangesDialog(
    checkbox: HTMLInputElement,
  ): void {
    const dialogRef = this.dialog.open(UnsavedChangesDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityName: 'Capacity',
        hasValidationErrors: this.hasAnyValidationErrors(),
        validationErrorMessage: this.getSaveButtonTooltip(),
      },
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result === 'discard') {
        // Discard changes and enable Overall mode
        this.restoreOriginalData();
        this.isOverallMode = true;
      } else if (result === 'save' && !this.hasAnyValidationErrors()) {
        // Save changes first, then enable Overall mode
        this.saveAndEnableOverallMode();
      } else {
        // Cancel, or a save that could not run: uncheck the checkbox. A refused save never
        // reaches onSaved, so leaving it ticked would show Global on while the mode is off.
        checkbox.checked = false;
      }
    });
  }

  /**
   * Reports a completed save, saying so when the entry window kept part of it out of
   * the payload. Silent success would be a lie: the reload that follows discards the
   * edits that were not sent.
   */
  private reportSaveOutcome(droppedLockedEdits: boolean): void {
    if (droppedLockedEdits) {
      this.snackbarService.showWarning(
        `The other periods were saved. ${this.entryWindowClosedMessage}`,
      );
      return;
    }

    this.snackbarService.showSuccess('Capacity settings saved successfully');
  }

  /**
   * Whether any period the user changed is one the entry window has since closed on.
   * Only reachable when the window closes mid-session, since the controls are disabled.
   */
  private hasLockedUnsavedChanges(): boolean {
    return this.periods.some(
      (period) => this.isPeriodLocked(period) && this.isPeriodChanged(period),
    );
  }

  /** Whether a period's status or notes differ from what the server last returned. */
  private isPeriodChanged(period: WeekPeriod): boolean {
    return this.isCapacityChangedAt(period.startDate.toISOString());
  }

  /** The same comparison keyed by period, for callers that only hold the key. */
  private isCapacityChangedAt(periodKey: string): boolean {
    const current = this.capacityData[periodKey];
    const original = this.originalCapacityData[periodKey];

    return (
      current?.capacityStatus?.code !== original?.capacityStatus?.code ||
      (current?.notes || '') !== (original?.notes || '') ||
      // Expected hours count as a change in their own right: editing only the hours and saving
      // used to exit edit mode without building a request at all, so the value was silently
      // dropped and came back empty on the next load.
      //
      // ?? not ||, so a change between 0 and null still registers - those are different states
      // and || would collapse both to "absent".
      (current?.expectedHours ?? null) !== (original?.expectedHours ?? null)
    );
  }

  /**
   * Builds bulk upsert requests for every period the user actually changed.
   *
   * Periods whose entry window has closed are excluded. Their controls are disabled,
   * but the payload is what the API judges, and it rejects the whole batch — writing
   * nothing — if any locked period is in it.
   */
  private buildChangedCapacityRequests(): CreateResourceCapacityRequest[] {
    const requests: CreateResourceCapacityRequest[] = [];

    // Iterate through all periods and check for changes
    this.periods.forEach((period) => {
      if (this.isPeriodLocked(period)) {
        return;
      }

      const periodKey = period.startDate.toISOString();
      const currentCapacity = this.capacityData[periodKey];
      // Needed on its own below, beyond the has-anything-changed test: an emptied field is only a
      // request to clear when it previously held a value. Compared with ?? rather than ||, so a
      // change between 0 and null still registers - those are different states, and || would
      // collapse both to "absent".
      const hasExpectedHoursChanged =
        (currentCapacity?.expectedHours ?? null) !==
        (this.originalCapacityData[periodKey]?.expectedHours ?? null);

      // Only include in save request if there are changes and a valid status exists
      // The same savable-status test the status rule uses, so the payload and the rule cannot
      // drift apart: a period the rule would flag is never sent.
      if (
        this.isCapacityChangedAt(periodKey) &&
        currentCapacity?.capacityStatus?.code &&
        this.hasSavableStatus(currentCapacity)
      ) {
        const request: CreateResourceCapacityRequest = {
          employeeId: this.employeeId(),
          date: this.periodStartAsUtc(period),
          capacityStatusCode: currentCapacity.capacityStatus.code,
          comments: currentCapacity.notes || undefined,
        };

        // Three outcomes, and the difference matters. Send the number when there is one; ask for an
        // explicit clear when the user emptied a field that held a value; send neither when hours
        // did not change, because an omitted field preserves what is stored.
        if (
          currentCapacity.expectedHours !== null &&
          currentCapacity.expectedHours !== undefined
        ) {
          request.expectedHours = currentCapacity.expectedHours;
        } else if (hasExpectedHoursChanged) {
          request.clearExpectedHours = true;
        }

        requests.push(request);
      }
    });

    return requests;
  }

  private saveAndEnableOverallMode(): void {
    this.saveChangedCapacities(() => {
      this.isOverallMode = true;
    }, false);
  }

  /**
   * Sends every period the user changed that the entry window still allows, then runs
   * onSaved. Both save entry points share this: they differ only in what follows a
   * successful write, and keeping two copies had already let them drift apart.
   */
  private saveChangedCapacities(
    onSaved: () => void = () => {},
    warnWhenNothingToSend = true,
  ): void {
    if (!this.employeeId()) {
      this.snackbarService.showError('Employee ID is required');
      this.discardPendingNavigation();
      return;
    }

    // Stop here rather than letting the API reject it. The server enforces the same rule, but a
    // rejected bulk save applies none of its intervals, so a single bad field would discard every
    // other change the user made.
    //
    // Guarding here rather than in onSaveCapacities so the saves triggered by navigation are
    // covered by it too - those reach the API through this same method.
    //
    // The same lock-aware check the exit paths use, for every rule, so the card never offers a
    // save this guard then refuses. A stale error on a period the entry window has since closed
    // on must not block the rest: that period is left out of the payload and reverted
    // afterwards.
    if (this.hasAnyValidationErrors()) {
      this.snackbarService.showError(this.getSaveButtonTooltip());
      this.discardPendingNavigation();
      return;
    }

    this.isSaving = true;

    const requests = this.buildChangedCapacityRequests();
    // Captured before the save, because the reload that follows discards these edits.
    const droppedLockedEdits = this.hasLockedUnsavedChanges();

    if (requests.length === 0) {
      this.isSaving = false;
      if (droppedLockedEdits) {
        this.snackbarService.showWarning(
          `That change could not be saved. ${this.entryWindowClosedMessage}`,
        );
        // Nothing reloads on this path, so put the unsendable edits back by hand or
        // they stay on screen looking entered and keep the card marked dirty.
        this.revertLockedPeriods();
      } else if (warnWhenNothingToSend) {
        this.snackbarService.showWarning('No changes to save');
      }
      onSaved();
      // A save requested on the way to another period still has to navigate, or the
      // stale direction fires on a later save and moves the user unexpectedly.
      this.flushPendingNavigation();
      return;
    }

    // Call the bulk upsert API
    this.capacityService
      .bulkUpsertCapacities(requests)
      .pipe(retryTransient())
      .subscribe({
        next: () => {
          this.reportSaveOutcome(droppedLockedEdits);
          if (this.isDestroyed) {
            return;
          }
          this.isSaving = false;
          onSaved();
          // A queued navigation reloads on its own, and loadCapacityData cancels any
          // request still in flight, so loading here too would only be aborted.
          if (!this.flushPendingNavigation()) {
          this.loadCapacityData();
          }
        },
        error: (error) => {
          if (isDevMode()) {
            console.error('Failed to save capacity settings:', error);
          }
          const errorMessage = this.getErrorMessage(
            error,
            'Failed to save capacity settings',
          );
          // Tell the user even if they have navigated on: the save did not happen.
          this.snackbarService.showError(errorMessage);
          if (this.isDestroyed) {
            return;
          }
          this.isSaving = false;
          this.discardPendingNavigation();
        },
      });
  }

  /**
   * The period's first day as UTC midnight. period.startDate is a local midnight, so
   * serialising it directly names the previous day in any UTC-positive zone — London
   * would send 2026-08-15T23:00Z for the period starting 2026-08-16, and the API would
   * normalise that Saturday back into the previous period.
   */
  private periodStartAsUtc(period: WeekPeriod): string {
    return new Date(
      this.calendarDay(period.startDate) * MILLISECONDS_PER_DAY,
    ).toISOString();
  }

  /**
   * Puts periods the entry window has closed on back to the server's values. Their
   * edits can never be sent, so leaving them on screen would misreport what is saved.
   */
  private revertLockedPeriods(): void {
    this.periods
      .filter((period) => this.isPeriodLocked(period))
      .forEach((period) => {
        const periodKey = period.startDate.toISOString();
        const original = this.originalCapacityData[periodKey];

        if (original) {
          // rawData carries the billing summary attached after load, which the
          // originals snapshot predates — keep the live one.
          this.capacityData[periodKey] = {
            ...JSON.parse(JSON.stringify(original)),
            rawData: this.capacityData[periodKey]?.rawData,
          };
        } else {
          delete this.capacityData[periodKey];
        }
        this.expectedHoursErrors[periodKey] = null;
        this.expectedHoursInput[periodKey] = this.expectedHoursTextFor(
          this.capacityData[periodKey],
        );
      });
  }

  onDiscardChanges(): void {
    this.restoreOriginalData();
    this.cdr.detectChanges();
  }

  /**
   * Puts the card back to what the server last returned and clears every validation error.
   * The one place a discard happens, whichever dialog or icon asked for it: the earlier
   * copies of this reset the notes errors but not the expected-hours ones, so a stale
   * hours error survived Discard and blocked the next edit session.
   */
  private restoreOriginalData(): void {
    if (this.isOverallMode) {
      // Restore original overall capacity
      this.overallCapacityStatus = this.originalOverallCapacityStatus;
    } else {
      // Restore original data for individual periods
      this.capacityData = JSON.parse(JSON.stringify(this.originalCapacityData));
    }
    this.resetExpectedHoursState();
  }

  /**
   * Drops the expected-hours errors and re-derives the typed text from `capacityData`, or a
   * discarded "40" would stay on screen over a period that is back to holding nothing.
   * Leaves `capacityData` alone: the no-change exits call this on data that already matches
   * the snapshot, and copying the snapshot over it would lose the billing summary attached
   * to `rawData` after load.
   */
  private resetExpectedHoursState(): void {
    this.expectedHoursErrors = {};
    this.expectedHoursInput = {};
    Object.entries(this.capacityData).forEach(([periodKey, capacity]) => {
      this.expectedHoursInput[periodKey] = this.expectedHoursTextFor(capacity);
    });
  }

  /** The field text for a stored value: empty for nothing reported, "0" for zero. */
  private expectedHoursTextFor(capacity: WeeklyCapacity | undefined): string {
    return capacity?.expectedHours === null ||
      capacity?.expectedHours === undefined
      ? ''
      : String(capacity.expectedHours);
  }

  onSaveCapacities(): void {
    // Handle overall mode save differently
    if (this.isOverallMode) {
      this.onSaveOverallCapacity();
      return;
    }

    this.saveChangedCapacities();
  }

  getCapacityForPeriod(period: WeekPeriod): WeeklyCapacity | null {
    const periodKey = period.startDate.toISOString();
    return this.capacityData[periodKey] || null;
  }

  getBillingInfo(period: WeekPeriod): any {
    const capacity = this.getCapacityForPeriod(period);
    if (!capacity || !(capacity.rawData as any)?.billingInfo) {
      return null;
    }
    return (capacity.rawData as any).billingInfo;
  }

  getStatusDotClass(period: WeekPeriod): string {
    const capacity = this.getCapacityForPeriod(period);
    const code = capacity?.capacityStatus?.code;
    if (!code) return 'status-dot-unknown';

    switch (code) {
      case CAPACITY_STATUS_CODES.AT_CAPACITY:
        return 'status-dot-at-capacity';
      case CAPACITY_STATUS_CODES.BELOW_CAPACITY:
        return 'status-dot-below-capacity';
      case CAPACITY_STATUS_CODES.OVER_CAPACITY:
        return 'status-dot-over-capacity';
      case CAPACITY_STATUS_CODES.NOT_AVAILABLE:
        return 'status-dot-not-available';
      case CAPACITY_STATUS_CODES.MIXED:
        return 'status-dot-mixed';
      default:
        return 'status-dot-default';
    }
  }

  isMixedStatus(period: WeekPeriod): boolean {
    const capacity = this.getCapacityForPeriod(period);
    return capacity?.capacityStatus?.code === CAPACITY_STATUS_CODES.MIXED;
  }

  getGradientId(period: WeekPeriod): string {
    return `gradient-${period.startDate.getTime()}`;
  }

  getGradientStops(
    period: WeekPeriod,
  ): Array<{ offset: string; color: string }> {
    const capacity = this.getCapacityForPeriod(period);
    if (!capacity || !capacity.rawData?.weeklyCapacities?.length) {
      return [];
    }

    const weeklyCapacities = capacity.rawData.weeklyCapacities;
    const stops: Array<{ offset: string; color: string }> = [];

    // Define the compression factor - how much to shrink the gradient
    // 0.6 means the gradient occupies 60% of the space (20% solid on each end)
    const compressionFactor = 0.4;
    const solidAreaPercent = ((1 - compressionFactor) / 2) * 100; // 20% on each side

    weeklyCapacities.forEach((week, index) => {
      const color = this.getStatusColorByCode(week.capacityStatus?.code);

      if (index === 0) {
        // First color: add stop at 0% and at the start of gradient area
        stops.push({ offset: '0%', color: color });
        stops.push({ offset: `${solidAreaPercent}%`, color: color });
      } else if (index === weeklyCapacities.length - 1) {
        // Last color: add stop at the end of gradient area and at 100%
        stops.push({ offset: `${100 - solidAreaPercent}%`, color: color });
        stops.push({ offset: '100%', color: color });
      } else {
        // Middle colors: compress into the gradient area
        const normalizedPosition = index / (weeklyCapacities.length - 1);
        const compressedPosition =
          solidAreaPercent + normalizedPosition * compressionFactor * 100;
        stops.push({ offset: `${compressedPosition}%`, color: color });
      }
    });

    return stops;
  }

  private getStatusColorByCode(code: string | undefined): string {
    switch (code) {
      case CAPACITY_STATUS_CODES.BELOW_CAPACITY:
        return '#68A149'; // Green (--light-green-success, matches .status-dot-below-capacity)
      case CAPACITY_STATUS_CODES.AT_CAPACITY:
        return '#d68232'; // Orange (--dark-orange-warning, matches .status-dot-at-capacity)
      case CAPACITY_STATUS_CODES.OVER_CAPACITY:
        return '#9f3223'; // Red (--dark-red-error, matches .status-dot-over-capacity)
      case CAPACITY_STATUS_CODES.NOT_AVAILABLE:
        return '#000000'; // Black (matches .status-dot-not-available)
      default:
        return '#9e9e9e'; // Gray for unknown (matches .status-dot-unknown stroke)
    }
  }

  getBadgeGradient(period: WeekPeriod): string | null {
    if (!this.isMixedStatus(period)) {
      return null;
    }

    const stops = this.getGradientStops(period);
    if (stops.length === 0) {
      return null;
    }

    const gradientStops = stops
      .map((stop) => `${stop.color} ${stop.offset}`)
      .join(', ');
    return `linear-gradient(to bottom, ${gradientStops})`;
  }

  /**
   * Whether the period is the one containing today, on the viewer's local clock. Used
   * for display only — the entry-window lock deliberately answers the same question on
   * the UTC calendar instead, because that is the calendar the API judges it on. See
   * isPeriodLocked.
   */
  isCurrentPeriod(period: WeekPeriod): boolean {
    // Compared at midnight, like every other period check: see startOfToday for why.
    const todayTime = this.startOfToday().getTime();
    const periodStart = period.startDate.getTime();
    const periodEnd = period.endDate.getTime();

    return todayTime >= periodStart && todayTime <= periodEnd;
  }

  /**
   * Whole days since the epoch for an ISO date the API sent, or null when it is absent
   * or unreadable. Deliberately not routed through parseApiDate, whose fallback is
   * today — as a cadence anchor that silently reads as "this period just started".
   */
  private apiDateToDay(value: string | null | undefined): number | null {
    if (!value) {
      return null;
    }

    const parts = this.parseApiDateParts(value);
    if (!parts) {
      if (isDevMode()) {
        console.error('Unreadable capacity interval start date:', value);
      }
      return null;
    }

    // parseApiDateParts has already rejected anything Date.UTC would roll over.
    const [year, month, day] = parts;
    return Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY;
  }

  /**
   * Whole days since the epoch for a calendar date, ignoring its time of day. Dates
   * the API sends are UTC calendar dates that parseApiDate rebuilds locally, so this
   * recovers the day the server meant regardless of the viewer's time zone.
   */
  private calendarDay(date: Date): number {
    return (
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) /
      MILLISECONDS_PER_DAY
    );
  }

  /**
   * Today on the UTC calendar. The API judges the entry window in UTC — its weeks run
   * Sunday 00:00 UTC to Saturday 23:59 UTC — so using the local calendar here would
   * make client and server disagree through the last hours of every local Saturday in
   * a UTC-negative time zone, and the client would offer an edit the API refuses.
   */
  private todayUtcDay(): number {
    return Math.floor(Date.now() / MILLISECONDS_PER_DAY);
  }

  /**
   * Where today sits in the current cadence period — the UTC day the period started on,
   * and how many whole days into it today is — or null when no multi-week cadence is
   * configured. Mirrors CalculateWeeksIntoInterval on the API, including the way the
   * cadence repeats in both directions from its anchor, so a future anchor lands on the
   * same period here as it does there.
   *
   * Both values come from one clock read so they cannot disagree across midnight.
   */
  private currentInterval(): { startDay: number; daysIn: number } | null {
    if (this.capacityInterval <= 1 || this.intervalAnchorDay === null) {
      return null;
    }

    const today = this.todayUtcDay();
    // Every entry-window binding funnels through here, so a render asks dozens of
    // times. Cached per UTC day: still closes the window mid-session, but every
    // binding in one render now agrees even across a midnight rollover.
    if (this.cachedInterval?.day === today) {
      return this.cachedInterval.interval;
    }

    const daysInInterval = this.capacityInterval * DAYS_IN_WEEK;
    const offset = today - this.intervalAnchorDay;
    const daysIn = ((offset % daysInInterval) + daysInInterval) % daysInInterval;
    const interval = { startDay: today - daysIn, daysIn };

    this.cachedInterval = { day: today, interval };
    return interval;
  }

  /**
   * True once the current multi-week period is past its first week. From that point
   * the API refuses every write touching the current period — capacity records and the
   * overall capacity override — until the next period starts, so both of those
   * controls have to close together.
   */
  isEntryWindowClosed(): boolean {
    const interval = this.currentInterval();
    return interval !== null && interval.daysIn >= DAYS_IN_WEEK;
  }

  /**
   * Capacity for the current multi-week period may only be entered during that
   * period's first week, so resources are trained to enter time early. Only the period
   * containing today is affected: past periods and forward planning stay open, and a
   * resource on a 1-week cadence is never locked.
   *
   * Mirrors CapacityService.EnsureCapacityEntryWindowOpen on the API, which refuses the
   * same writes. This is an affordance, not the enforcement: the server has the final
   * say and its message is surfaced if a stale tab or a skewed browser clock lets a
   * refused write through. It does not reuse isCurrentPeriod, which reads the local
   * clock — see there.
   */
  isPeriodLocked(period: WeekPeriod): boolean {
    const interval = this.currentInterval();
    if (interval === null || interval.daysIn < DAYS_IN_WEEK) {
      return false;
    }

    return this.calendarDay(period.startDate) === interval.startDay;
  }

  /** Tooltip for a period the closed entry window applies to; empty when it does not. */
  getPeriodLockTooltip(period: WeekPeriod): string {
    return this.isPeriodLocked(period) ? this.entryWindowClosedMessage : '';
  }

  /** Tooltip for the controls that act on the current period as a whole. */
  getEntryWindowTooltip(): string {
    return this.isEntryWindowClosed() ? this.entryWindowClosedMessage : '';
  }

  /** Whether a period's capacity status and notes may be edited right now. */
  isPeriodEditDisabled(period: WeekPeriod): boolean {
    return (
      this.isSaving ||
      this.isPeriodLoading ||
      this.isOverallMode ||
      this.isPeriodLocked(period)
    );
  }

  onCapacityStatusChange(period: WeekPeriod, statusCode: string): void {
    this.selectedPeriodKey = period.startDate.toISOString();
    const periodKey = period.startDate.toISOString();

    // Find the LookupValue for this code
    const lookupValue =
      this.capacityStatusLookups.find((lv) => lv.code === statusCode) || null;

    if (!this.capacityData[periodKey]) {
      this.capacityData[periodKey] = this.createCapacityData(
        period,
        lookupValue,
        '',
      );
    } else {
      this.capacityData[periodKey].capacityStatus = lookupValue;
    }
  }

  onNotesChange(period: WeekPeriod, notes: string): void {
    this.selectedPeriodKey = period.startDate.toISOString();
    const periodKey = period.startDate.toISOString();

    // Enforce 1000 character limit
    if (notes.length > 1000) {
      notes = notes.substring(0, 1000);
    }

    if (!this.capacityData[periodKey]) {
      // Create a new entry without a status (user must select one)
      this.capacityData[periodKey] = this.createCapacityData(
        period,
        null,
        notes,
      );
    } else {
      this.capacityData[periodKey].notes = notes;
    }
    // Notes changed on a period with no savable status are reported by hasValidationError,
    // which derives the rule from the data rather than recording it here.
  }

  isSelectedPeriod(period: WeekPeriod): boolean {
    return this.selectedPeriodKey === period.startDate.toISOString();
  }

  onSelectPeriod(period: WeekPeriod): void {
    this.selectedPeriodKey = period.startDate.toISOString();
  }

  onCardFocusIn(period: WeekPeriod): void {
    this.selectedPeriodKey = period.startDate.toISOString();
  }

  onCardFocusOut(
    period: WeekPeriod,
    event: FocusEvent,
    cardElement: HTMLElement,
  ): void {
    const nextTarget = event.relatedTarget as HTMLElement | null;
    // If focus is leaving the card and not moving to another element inside the same card, clear selection
    if (!nextTarget || !cardElement.contains(nextTarget)) {
      if (this.selectedPeriodKey === period.startDate.toISOString()) {
        this.selectedPeriodKey = null;
      }
    }
  }

  /**
   * Whether a period needs a capacity status before the card can save: it was changed (notes
   * or hours - the dropdown cannot be emptied) but has no status the save could send. The save
   * only sends periods with a savable status, so without this rule hours typed on an empty
   * period were silently dropped with "No changes to save", and an hours-only edit on a MIXED
   * period was sent and rejected after the card had exited.
   *
   * Derived rather than recorded, so it needs no clearing: choosing a status, reverting the
   * change, discarding or reloading all make it false. Skips locked periods, which are left out
   * of the save and whose dropdown is disabled, so a message there could not be acted on.
   */
  hasValidationError(period: WeekPeriod): boolean {
    if (this.isPeriodLocked(period)) {
      return false;
    }
    const periodKey = period.startDate.toISOString();
    return (
      this.isCapacityChangedAt(periodKey) &&
      !this.hasSavableStatus(this.capacityData[periodKey])
    );
  }

  /** The message under a period's status dropdown, or null. */
  getStatusFieldError(period: WeekPeriod): string | null {
    return this.hasValidationError(period) ? STATUS_REQUIRED_FIELD_MESSAGE : null;
  }

  /**
   * Whether a period holds a status the API will accept. UNKNOWN and MIXED are system-generated
   * and refused by its ValidCapacityStatusAttribute, so neither counts.
   */
  private hasSavableStatus(capacity: WeeklyCapacity | undefined): boolean {
    const code = capacity?.capacityStatus?.code;
    return (
      !!code &&
      code !== CAPACITY_STATUS_CODES.UNKNOWN &&
      code !== CAPACITY_STATUS_CODES.MIXED
    );
  }

  /**
   * Whether anything on screen would stop a save. Every way out of edit mode asks this before
   * saving, and so does the save itself, so it has to know every rule the save enforces — the
   * expected-hours rule was missing, and the card exited on a save that then refused to run
   * (bug 6952).
   */
  hasAnyValidationErrors(): boolean {
    return this.hasChangeWithoutStatus() || this.hasInvalidExpectedHours();
  }

  getSaveButtonTooltip(): string {
    return [
      this.hasChangeWithoutStatus() ? STATUS_REQUIRED_MESSAGE : '',
      this.hasInvalidExpectedHours() ? EXPECTED_HOURS_INVALID_MESSAGE : '',
    ]
      .filter((message) => message !== '')
      .join(' ');
  }

  /**
   * Whether any editable period needs a status. Ignored in Global mode for the same reason as
   * the hours rule: the period fields are hidden and the overall save never reads them.
   */
  private hasChangeWithoutStatus(): boolean {
    if (this.isOverallMode) {
      return false;
    }
    return this.periods.some((period) => this.hasValidationError(period));
  }

  /**
   * Whether an editable period's expected hours are invalid: a typed value that failed, or a
   * changed period the office requires hours for that has none. The one definition of the
   * rule: the exit paths and the save guard both ask it, so they can never disagree about
   * whether a save will run.
   *
   * Same lock exclusion as the status rule. Ignored in Global mode, where the period fields
   * are hidden and the overall save never reads them: an error the user cannot see must not
   * dead-end the card.
   */
  private hasInvalidExpectedHours(): boolean {
    if (this.isOverallMode) {
      return false;
    }
    return this.periods.some(
      (period) =>
        !this.isPeriodLocked(period) && this.hasExpectedHoursError(period),
    );
  }

  getStatusTooltip(period: WeekPeriod): string {
    const capacity = this.getCapacityForPeriod(period);
    if (!capacity || !capacity.capacityStatus) {
      return 'No capacity status selected';
    }

    if (capacity.capacityStatus.code === CAPACITY_STATUS_CODES.MIXED) {
      return this.getMixedStatusTooltip(capacity);
    }

    return capacity.capacityStatus.name || 'Unknown';
  }

  private getMixedStatusTooltip(capacity: WeeklyCapacity): string {
    if (!capacity.rawData?.weeklyCapacities?.length) {
      return 'Mixed capacity status';
    }

    const lines = ['Capacity has a mixed status'];

    capacity.rawData.weeklyCapacities.forEach((week, index) => {
      const statusLabel = week.capacityStatus?.name || 'Unknown';
      lines.push(`Week ${index + 1} is ${statusLabel}`);
    });

    return lines.join('\n');
  }

  getStatusLabel(period: WeekPeriod): string {
    const capacity = this.getCapacityForPeriod(period);
    return capacity?.capacityStatus?.name || 'Unknown';
  }

  getNotesCharacterCount(period: WeekPeriod): number {
    const capacity = this.getCapacityForPeriod(period);
    return capacity?.notes?.length || 0;
  }

  getNotesCharactersRemaining(period: WeekPeriod): number {
    const capacity = this.getCapacityForPeriod(period);
    const currentLength = capacity?.notes?.length || 0;
    return 1000 - currentLength;
  }

  /**
   * True when the stored notes are longer than the column allows.
   *
   * Not reachable by typing - onNotesChange truncates at 1000 and the textarea carries maxlength -
   * so this only fires for notes that arrived from the API already over the limit. Being *at* the
   * limit is a different, valid state: see isNotesAtCharacterLimit.
   */
  hasNotesError(period: WeekPeriod): boolean {
    const capacity = this.getCapacityForPeriod(period);
    const notesLength = capacity?.notes?.length || 0;
    return notesLength > 1000;
  }

  /**
   * True when the notes have exactly filled the 1000-character limit.
   *
   * Deliberately separate from hasNotesError, and phrased as a notice rather than a failure: the
   * content is valid and saveable, the user has simply run out of room. Typing further silently
   * drops characters, so saying so is the point - without it the only feedback is a counter that
   * stops moving.
   */
  isNotesAtCharacterLimit(period: WeekPeriod): boolean {
    const capacity = this.getCapacityForPeriod(period);
    return (capacity?.notes?.length || 0) === 1000;
  }

  // ===== Expected period hours =====

  /**
   * Records what the user typed into a period's expected-hours field and validates it.
   *
   * The raw text is kept so the field can echo it back, while `capacityData` holds the parsed
   * number the save uses. An empty field parses to null - which the save turns into an explicit
   * clear rather than an omission, because omitting the field means "leave it alone".
   */
  onExpectedHoursChange(period: WeekPeriod, rawValue: string): void {
    const periodKey = period.startDate.toISOString();
    this.selectedPeriodKey = periodKey;
    this.expectedHoursInput[periodKey] = rawValue;

    if (!this.capacityData[periodKey]) {
      this.capacityData[periodKey] = this.createCapacityData(period, null, '');
    }

    const trimmed = rawValue.trim();

    if (trimmed === '') {
      this.capacityData[periodKey].expectedHours = null;
      // Not an error of its own. Whether an empty field matters depends on whether the period
      // will be sent, which missingRequiredHoursError decides; recording "required" here used
      // to block every other save when an untouched period was typed in and cleared.
      this.expectedHoursErrors[periodKey] = null;
      return;
    }

    // Deliberately not parseInt: it happily reads "40abc" as 40 and "4.5" as 4, so a typo would be
    // silently accepted as a different number than the one on screen.
    const parsed = Number(trimmed);

    if (!Number.isInteger(parsed)) {
      this.capacityData[periodKey].expectedHours = null;
      this.expectedHoursErrors[periodKey] = 'Enter a whole number of hours';
      return;
    }

    if (parsed < 0 || parsed > 999) {
      this.capacityData[periodKey].expectedHours = null;
      // Worded to match the server's own [Range] message, so a value rejected client-side and one
      // rejected by the API read the same.
      this.expectedHoursErrors[periodKey] =
        'Expected hours must be between 0 and 999';
      return;
    }

    this.capacityData[periodKey].expectedHours = parsed;
    this.expectedHoursErrors[periodKey] = null;
  }

  /** The raw text for a period's field, so it survives re-render mid-edit. */
  getExpectedHoursInput(period: WeekPeriod): string {
    return this.expectedHoursInput[period.startDate.toISOString()] ?? '';
  }

  /**
   * The message to show under a period's hours field, or null. A typed-value error wins, so
   * "Enter a whole number of hours" is not replaced by the required message.
   */
  getExpectedHoursError(period: WeekPeriod): string | null {
    return (
      this.expectedHoursErrors[period.startDate.toISOString()] ??
      this.missingRequiredHoursError(period)
    );
  }

  /**
   * The required message for a period the save would send without hours, which the API would
   * reject. Mirrors buildChangedCapacityRequests: a period is sent when it is unlocked, changed
   * and has a status. The API accepts one that omits hours only if some are already stored, and
   * the loaded value here is that stored interval total, so null means none.
   *
   * Derived rather than recorded, so it needs no clearing: entering hours, reverting the
   * change, discarding or reloading all make the condition false. It also implies the card is
   * dirty, so the X icon always reaches the unsaved-changes dialog rather than the plain exit.
   *
   * A period without a savable status (none, UNKNOWN or MIXED) is not sent, and is reported by
   * the status rule in hasValidationError instead, so the two messages never both appear.
   */
  private missingRequiredHoursError(period: WeekPeriod): string | null {
    if (!this.expectedHoursRequired || this.isPeriodLocked(period)) {
      return null;
    }

    const periodKey = period.startDate.toISOString();
    const capacity = this.capacityData[periodKey];

    const wouldBeSentWithoutHours =
      this.hasSavableStatus(capacity) &&
      this.isCapacityChangedAt(periodKey) &&
      (capacity.expectedHours === null || capacity.expectedHours === undefined);

    return wouldBeSentWithoutHours ? EXPECTED_HOURS_REQUIRED_MESSAGE : null;
  }

  hasExpectedHoursError(period: WeekPeriod): boolean {
    return this.getExpectedHoursError(period) !== null;
  }

  /**
   * The stored value for display outside edit mode. Returns null rather than 0 when nothing was
   * reported, so the template can tell "no hours entered" from "zero hours entered".
   */
  getExpectedHoursDisplay(period: WeekPeriod): number | null {
    return this.getCapacityForPeriod(period)?.expectedHours ?? null;
  }

  /**
   * True when a period's reported expected hours should stand in for the billed figure.
   *
   * Current and future periods only. For the current period the expected hours are the resource's
   * own forecast for a period still in progress, so they say more than a part-week billing total
   * that will keep moving - AC 5c calls for exactly that override. Future periods have no billing
   * data at all, so expected hours are the only thing there is to show (AC 5d).
   *
   * Past periods keep their billed figure as the headline: what someone forecast for a finished
   * period is history, and what they actually billed is the number the period is judged on. The
   * forecast still shows there, through its own line above the billed total; see
   * shouldShowPastExpectedHours.
   */
  shouldShowExpectedHours(period: WeekPeriod): boolean {
    if (this.getExpectedHoursDisplay(period) === null) {
      return false;
    }
    return this.isCurrentPeriod(period) || this.isFuturePeriod(period);
  }

  /**
   * True when a finished period should carry an expected-hours line above its billed total.
   *
   * Reported hours always show, so the forecast can be read against the actual. When nothing was
   * reported the line shows only if the office requires hours: the absence of a forecast is then
   * the information, and the template renders it as "No expected hours reported".
   *
   * The requirement is the resource's current one, not the one in force when the period was
   * live: the capacity read carries a single flag for the whole response.
   *
   * A week whose data did not load says nothing. A successful load creates an entry for every
   * week, even one with nothing stored, so a missing entry means the load failed. Navigating
   * swaps in the new weeks before the fetch runs, and a failed fetch leaves the last good load's
   * requirement in place, so without this check every past week would claim nothing was
   * reported.
   */
  shouldShowPastExpectedHours(period: WeekPeriod): boolean {
    if (!this.isPastPeriod(period) || !this.getCapacityForPeriod(period)) {
      return false;
    }
    return (
      this.expectedHoursRequired ||
      this.getExpectedHoursDisplay(period) !== null
    );
  }

  /**
   * The billing skeleton: a current or past period whose billed figures are still being fetched.
   * Never while expected hours stand in for the billed total, since then there is nothing to wait
   * for.
   */
  showsBillingSkeleton(period: WeekPeriod): boolean {
    return (
      !this.shouldShowExpectedHours(period) &&
      this.isBillingDataLoading &&
      !this.getBillingInfo(period) &&
      !this.isFuturePeriod(period)
    );
  }

  /** The billed total and its breakdown, once loaded and not overridden by expected hours. */
  showsBillingTotals(period: WeekPeriod): boolean {
    return (
      !this.shouldShowExpectedHours(period) &&
      !this.isBillingDataLoading &&
      !!this.getBillingInfo(period)
    );
  }

  /**
   * Whether the hours column beside the status badge has anything to render for a period outside
   * edit mode. Defined as the disjunction of the lines the column can hold, so the gate and its
   * contents cannot drift apart. The column is one flex child; rendering it empty would still take
   * up the row gap.
   */
  showsHoursColumn(period: WeekPeriod): boolean {
    return (
      this.shouldShowExpectedHours(period) ||
      this.shouldShowPastExpectedHours(period) ||
      this.showsBillingSkeleton(period) ||
      this.showsBillingTotals(period)
    );
  }

  private notesAtLimitPeriod: string | null = null;
  private lastLimitWarningTime = 0;

  isNotesAtLimit(period: WeekPeriod): boolean {
    return this.notesAtLimitPeriod === period.startDate.toISOString();
  }

  onNotesKeydown(period: WeekPeriod, event: KeyboardEvent): void {
    const capacity = this.getCapacityForPeriod(period);
    const currentLength = capacity?.notes?.length || 0;

    // Check if they're at the limit and trying to type a character (not deleting/navigating)
    const isCharacterKey = event.key.length === 1;
    const isAtLimit = currentLength >= 1000;
    const isNotDeletingOrNavigating =
      !event.ctrlKey &&
      !event.metaKey &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'ArrowUp' &&
      event.key !== 'ArrowDown';

    if (isCharacterKey && isAtLimit && isNotDeletingOrNavigating) {
      // Trigger the shake animation
      const periodKey = period.startDate.toISOString();
      this.notesAtLimitPeriod = periodKey;

      // Show snackbar warning (throttled to once every 3 seconds)
      const now = Date.now();
      if (now - this.lastLimitWarningTime > 3000) {
        this.snackbarService.showWarning('Character limit reached (1000)');
        this.lastLimitWarningTime = now;
      }

      // Remove the animation class after it completes
      setTimeout(() => {
        if (this.notesAtLimitPeriod === periodKey) {
          this.notesAtLimitPeriod = null;
        }
      }, 500);
    }
  }

  getStatusBadgeClass(period: WeekPeriod): string {
    const capacity = this.getCapacityForPeriod(period);
    if (!capacity || !capacity.capacityStatus) {
      return 'badge-unknown';
    }

    return this.getStatusBadgeClassForStatus(capacity.capacityStatus);
  }

  getStatusBadgeClassForStatus(status: LookupValue | null): string {
    const code = status?.code;
    if (!code) {
      return 'badge-unknown';
    }

    switch (code) {
      case CAPACITY_STATUS_CODES.BELOW_CAPACITY:
        return 'badge-below-capacity';
      case CAPACITY_STATUS_CODES.AT_CAPACITY:
        return 'badge-at-capacity';
      case CAPACITY_STATUS_CODES.OVER_CAPACITY:
        return 'badge-over-capacity';
      case CAPACITY_STATUS_CODES.NOT_AVAILABLE:
        return 'badge-not-available';
      case CAPACITY_STATUS_CODES.MIXED:
        return 'badge-mixed';
      default:
        return 'badge-unknown';
    }
  }

  getStatusLabelForStatus(status: LookupValue | null): string {
    return status?.name || 'Unknown';
  }

  /**
   * Get status dot class for a given status value (for Overall capacity)
   */
  getStatusDotClassForStatus(status: LookupValue | null): string {
    const code = status?.code;
    if (!code) return 'status-dot-unknown';

    switch (code) {
      case CAPACITY_STATUS_CODES.AT_CAPACITY:
        return 'status-dot-at-capacity';
      case CAPACITY_STATUS_CODES.BELOW_CAPACITY:
        return 'status-dot-below-capacity';
      case CAPACITY_STATUS_CODES.OVER_CAPACITY:
        return 'status-dot-over-capacity';
      case CAPACITY_STATUS_CODES.NOT_AVAILABLE:
        return 'status-dot-not-available';
      default:
        return 'status-dot-default';
    }
  }

  /**
   * Get status tooltip for a given status value (for Overall capacity)
   */
  getStatusTooltipForStatus(status: LookupValue | null): string {
    return status?.name || 'No status set';
  }

  /**
   * Turns an HTTP error into something worth showing the user: the shared rule (describeApiError),
   * which is how the rejection telling a resource their office requires expected hours — the only
   * instruction they get — reaches them, plus this card's one rule of its own: a 404 names the
   * operation that failed.
   */
  private getErrorMessage(error: unknown, defaultMessage: string): string {
    if (toApiError(error).isNotFound) {
      return `${defaultMessage} - Not found`;
    }
    return describeApiError(error, defaultMessage);
  }

  /**
   * Check if user has an overall capacity override and load it if they do
   * Called when entering edit mode to determine initial state
   * Uses the currentCapacityStatusOverride from the already-loaded capacity data
   */
  private checkAndLoadOverallCapacity(): void {
    if (!this.employeeId()) {
      this.isCheckingOverallMode = false;
      return;
    }

    // Use the override data that was already loaded from the capacity response
    if (this.overallCapacityStatus) {
      // User has an overall capacity set - switch to Overall mode
      this.isOverallMode = true;
    } else {
      // No overall capacity - stay on the per-period cards
      this.isOverallMode = false;
    }

    this.isCheckingOverallMode = false; // Done loading
  }

  /**
   * Check if there are unsaved changes to overall capacity
   */
  hasOverallUnsavedChanges(): boolean {
    return (
      this.overallCapacityStatus?.code !==
      this.originalOverallCapacityStatus?.code
    );
  }

  /**
   * Show confirmation dialog when turning off Overall mode via switch
   * Warns user that overall capacity will be deleted
   */
  private showClearOverallCapacityDialogForSwitch(
    checkbox: HTMLInputElement,
  ): void {
    // Determine the message based on whether there are unsaved changes
    const hasUnsavedChanges = this.hasOverallUnsavedChanges();
    const message = hasUnsavedChanges
      ? 'You have unsaved changes. Turning off Overall mode will discard these changes and clear your overall capacity settings. Do you want to continue?'
      : 'Turning off Overall mode will clear your overall capacity settings. Do you want to continue?';

    const dialogData: ConfirmationDialogData = {
      title: 'Turn Off Overall Mode?',
      message: message,
      confirmText: 'Continue',
      cancelText: 'Cancel',
      icon: 'warning',
      iconColor: '#f59e0b',
      highlightColor: '#fef3c7',
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true,
      autoFocus: false,
    });

    // Hook into the confirm button click to handle loading state
    const originalOnConfirm = dialogRef.componentInstance.onConfirm.bind(
      dialogRef.componentInstance,
    );
    dialogRef.componentInstance.onConfirm = () => {
      originalOnConfirm();

      // User confirmed - cancel overall capacity and switch to interval mode
      this.isOverallMode = false;

      // Cancel the capacity override if it exists
      if (this.originalOverallCapacityStatus) {
        this.capacityService
          .cancelCapacityOverride(this.employeeId())
          .pipe(retryTransient())
          .subscribe({
            next: () => {
              // Close first: this dialog is disableClose, so leaving it open would
              // strand a modal with no way out if the card is already gone.
              dialogRef.close(true);
              if (this.isDestroyed) {
                return;
              }
              // Clear the override data
              this.overallCapacityStatus = null;
              this.originalOverallCapacityStatus = null;

              // Reload capacity data to reflect the removal of overall capacity
              this.loadCapacityData();
            },
            error: (error) => {
              if (isDevMode()) {
                console.error('Failed to cancel capacity override:', error);
              }
              const errorMessage = this.getErrorMessage(
                error,
                'Failed to clear overall capacity',
              );
              this.snackbarService.showError(errorMessage);
              // Release the dialog's buttons whatever happened to the card, or its
              // Cancel is left disabled behind a disableClose backdrop.
              dialogRef.componentInstance.isLoading = false;
              if (this.isDestroyed) {
                return;
              }
              // Restore Overall mode on error
              this.isOverallMode = true;
              checkbox.checked = true;
            },
          });
      } else {
        // No override to delete, just switch modes
        this.overallCapacityStatus = null;
        this.originalOverallCapacityStatus = null;

        // Close this dialog
        dialogRef.close(true);
      }
    };

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed === false) {
        // User cancelled - revert the checkbox
        checkbox.checked = true;
        this.isOverallMode = true;
      }
    });
  }

  /**
   * Handle overall capacity status change
   */
  onOverallCapacityChange(statusCode: string): void {
    const lookupValue =
      this.capacityStatusLookups.find((lv) => lv.code === statusCode) || null;
    this.overallCapacityStatus = lookupValue;
  }

  /**
   * Save overall capacity override
   */
  onSaveOverallCapacity(): void {
    const employeeId = this.employeeId();
    if (!employeeId || !this.overallCapacityStatus?.code) {
      this.snackbarService.showError('Please select a capacity status');
      this.discardPendingNavigation();
      return;
    }

    // The override defaults to the current period's first Sunday, so the API applies
    // the same entry-window rule to it as to per-period capacity.
    if (this.isEntryWindowClosed()) {
      this.snackbarService.showError(this.entryWindowClosedMessage);
      // Nothing was written, so do not leave the rejected value looking accepted.
      this.overallCapacityStatus = this.originalOverallCapacityStatus;
      this.discardPendingNavigation();
      return;
    }

    this.isSaving = true;
    const backendStatusCode = this.overallCapacityStatus.code;

    this.capacityService
      .upsertCapacityOverride(employeeId, backendStatusCode)
      .pipe(retryTransient())
      .subscribe({
        next: (response) => {
          this.snackbarService.showSuccess(
            'Overall capacity saved successfully',
          );
          if (this.isDestroyed) {
            return;
          }
          this.isSaving = false;
          this.originalOverallCapacityStatus = this.overallCapacityStatus;

          // A queued navigation reloads on its own, so only load when there is none
          if (!this.flushPendingNavigation()) {
          this.loadCapacityData();
          }
        },
        error: (error) => {
          if (isDevMode()) {
            console.error('Failed to save overall capacity:', error);
          }
          const errorMessage = this.getErrorMessage(
            error,
            'Failed to save overall capacity',
          );
          this.snackbarService.showError(errorMessage);
          if (this.isDestroyed) {
            return;
          }
          this.isSaving = false;
          this.discardPendingNavigation();
        },
      });
  }
}
